
import Vue, { PropType, Component } from 'vue';
import Api from '@/core/api';
import Viewers from './tabs/Viewers/Viewers.vue';
import Qna from './tabs/Qna/Qna.vue';
import Polls from './tabs/Polls/Polls.vue';
import Translations from './tabs/Translations/Translations.vue';
import Stream from './tabs/Stream/Stream.vue';
import { Unsubscriber } from '@/core/event-bus';
import { get, isFunction } from 'lodash';
import { SideBarConfig, Theme } from './side-bar-config.interface';
import IconComponent, { Icon } from '@/common/icon/Icon.vue';
import IconButton from '@/common/icon-button/IconButton.vue';
import SideBarNotification from './SideBarNotification.vue';
import { Portal } from 'portal-vue';
import { PlayerState } from '@/core/player-state';
import { PollType } from '@/widgets/polls/polls.controller';
import { TRANSITION_DURATION } from './constants';

interface Tab {
    id: string,
    component: Component,
    icon: string,
}

export default Vue.extend({
    name: 'SideBar',

    components: {
        Viewers,
        Qna,
        IconButton,
        Polls,
        SideBarNotification,
        Portal, // Vue.use(PortalVue) does not seem to work with web components,
        Icon: IconComponent,
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        },
        config: {
            type: Object as PropType<SideBarConfig>,
            required: true
        },
        isSideBarBelow: {
            type: Boolean,
            required: true
        },
        noPlayer: {
            type: Boolean,
            required: true
        },
        isWebinar: {
            type: Boolean,
            required: true
        },
    },

    data() {
        const tabs = [
            {
                id: 'qna',
                component: Qna,
                icon: Icon.question
            }, {
                id: 'polls',
                component: Polls,
                icon: Icon.ranking
            }, {
                id: 'translations',
                component: Translations,
                icon: Icon.translations
            }, {
                id: 'viewers',
                component: Viewers,
                icon: Icon.participants
            }, {
                id: 'stream',
                component: Stream,
                icon: Icon.playCircle
            }
        ];
        return {
            unsubscribers: [] as Unsubscriber[],
            tabs,
            opened: true,
            pollNotification: null as unknown,
            qnaNotification: null as unknown,
            openedTab: undefined as Tab | undefined,
            previouslyOpenedTab: undefined as Tab | undefined,
            isClientFullScreen: false,
            isVideoElementFullScreen: false,
            isSideBarExpanded: false
        };
    },

    computed: {
        configuredTabs(): Array<Tab> {
            return this.tabs.filter(tab => this.tabsConfig[tab.id]);
        },

        configuredTabsInMainView(): Array<Tab> {
            return this.openedTab ? this.configuredTabs : [];
        },

        tabsConfig(): Record<string, unknown> {
            const tabsConfig = {} as Record<string, unknown>;
            for (const tab of this.tabs) {
                tabsConfig[tab.id] = (this.config?.components ?? []).find((c: Record<string, unknown>) => c.id === tab.id);
            }
            return tabsConfig;
        },

        showCloseButton():boolean {
            // TODO: show close button based on client config
            return false;
        },

        tabCount():number {
            return this.config?.components?.length ?? 0;
        },

        showToggler():boolean {
            return !!this.tabCount && !this.noPlayer;
        },

        showFullscreenToggler(): boolean {
            return !!this.config?.showFullscreenToggler && !this.isSideBarBelow;
        },

        togglerIcon(): Icon {
            return this.opened ? Icon.chevronRight : Icon.chevronLeft;
        },

        fullscreenIcon(): Icon {
            return this.isClientFullScreen ? Icon.minimize : Icon.maximize;
        },

        sideBarNotificationPortalId(): string {
            return '' + this.api.getPortalId('SIDEBAR_NOTIFICATION');
        },

        showPollNotification(): boolean {
            return this.openedTab?.id !== 'polls' && !!this.pollNotification;
        },

        showQnaNotification(): boolean {
            return this.openedTab?.id !== 'qna' && !!this.qnaNotification;
        },

        notificationDots(): Array<string> {
            return [
                ...(this.showPollNotification ? ['polls'] : []),
                ...(this.showQnaNotification ? ['qna'] : [])
            ];
        },

        theme(): Theme {
            return this.config?.theme ?? {} as Theme;
        },

        isPrivateQna(): boolean {
            return !!this.config?.isPrivateQna;
        }
    },

    watch: {
        isSideBarBelow: {
            immediate: true,
            handler() {
                if (this.isSideBarBelow) {
                    this.opened = true;
                }
            }
        },

        config() {
            this.resetOpenedTab();
        },

        'tabsConfig': {
            deep: true,
            handler(next, prev) {
                const prevQuestions = get(prev, 'qna.questions', []);
                const nextQuestions = get(next, 'qna.questions', []);

                if (prevQuestions.length !== nextQuestions.length) {
                    this.qnaNotification = nextQuestions[nextQuestions.length - 1];
                }
            }
        },
    },

    mounted() {
        const { unsubscriber, state } = this.api.onStateChange(
            (partial: Partial<PlayerState>) => {
                this.isVideoElementFullScreen = get(partial, 'isVideoElementFullScreen', this.isVideoElementFullScreen);
                this.isSideBarExpanded = get(this.config, 'components.polls.preventExpand', false)
                    ? false
                    : (
                        get(this, 'tabsConfig.polls.question') &&
                        get(this, 'tabsConfig.polls.pollType') === PollType.WORD_CLOUD &&
                        get(partial, 'isSidebarExpanded', this.isSideBarExpanded)
                    );
            }
        );

        this.isVideoElementFullScreen = get(state, 'isVideoElementFullScreen', this.isVideoElementFullScreen);

        this.unsubscribers.push(unsubscriber);

        this.unsubscribers.push(
            this.api.onClientAction((payload, context?) => {
                if (context === 'clientFullscreen') {
                    this.isClientFullScreen = payload as boolean;
                }
            }),
            this.api.onCustomAction((payload, context) => {
                if (context === 'sidebarToggle') {
                    this.toggleSideBar();
                }
            }),
            this.api.onClientAction((payload: unknown, context?: string) => this.onClientAction(payload, context))
        );

        this.resetOpenedTab();
    },

    beforeDestroy() {
        for (const unsubscriber of this.unsubscribers) {
            if (isFunction(unsubscriber)) {
                unsubscriber();
            }
        }
    },

    methods: {
        onClientAction(payload: unknown, context?: string) {
            if (context !== 'sideBarNotification') {
                return;
            }
            const destTab = get(payload, 'toTab');
            if (!this.isVideoElementFullScreen && this.opened && destTab === this.openedTab?.id) {
                return;
            }
            const immediateTabSwitch = get(payload, 'immediateTabSwitch');
            if (immediateTabSwitch) {
                return this.pollNotificationClick(destTab);
            }

            this.pollNotification = payload;
            this.api.customAction(payload, 'sideBarNotification');
        },

        resetOpenedTab() {
            if (this.openedTab && this.tabsConfig[this.openedTab.id]) {
                return;
            }
            this.openedTab = this.tabs.find((tab) => { return !!this.tabsConfig[tab.id]; }) as Tab;
            this.isSideBarExpanded = false;
        },

        changeTab(newTab: Tab) {
            this.opened = true;
            const oldTab = this.openedTab;
            this.openedTab = newTab;
            if (get(this, 'pollNotification.toTab') === this.openedTab.id) {
                this.pollNotification = null;
                this.api.customAction(null, 'sideBarNotification');
            }
            if (this.openedTab.id === 'qna' || oldTab?.id === 'qna') {
                this.qnaNotification = null;
            }
            this.api.collapseSidebar('sidebar.wordcloud');
        },

        pollNotificationClick(newTabId:string) {
            this.api.exitFullScreen();
            this.changeTab(this.tabs.find((t) => t.id === newTabId) as Tab);
            this.scrollToSideBar();
        },

        toggleSideBar() {
            if (this.opened) {
                this.api.collapseSidebar('sidebar.wordcloud');
                this.opened = false;
                if (this.openedTab?.id) {
                    this.previouslyOpenedTab = this.openedTab;
                }
                setTimeout(() => this.openedTab = undefined, TRANSITION_DURATION);
            } else {
                this.opened = true;
                if (this.previouslyOpenedTab?.id) {
                    this.changeTab(this.previouslyOpenedTab);
                } else {
                    this.resetOpenedTab();
                }
            }
        },

        toggleFullscreen() {
            this.api.customAction(null, 'clientFullscreen');
        },

        close() {
            // TODO: emit a close event to client
        },

        scrollToSideBar() {
            setTimeout(() => {
                const element = this.$el as HTMLElement;
                const rect = this.$el.getBoundingClientRect();

                const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
                const viewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
                const isVisible =
                    rect.top >= 0 &&
                    rect.left >= 0 &&
                    rect.top + rect.height * 0.5 <= viewportHeight &&
                    rect.left + rect.width * 0.5 <= viewportWidth;

                if (isVisible) {
                    return;
                }

                console.log('[SideBar] sidebar is out of view, scrolling to it');

                window.scrollTo({
                    top: element.offsetTop - viewportHeight / 4,
                    left: element.offsetLeft - viewportWidth / 4,
                    behavior: 'smooth'
                });
            }, 100);
        }
    }
});
